// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState } from "react"
import { Check, Copy } from "lucide-react"
import { cn } from "@sglara/cn"

export interface CopyableIdProps {
  id: string
  label: string
  className?: string
}

export const CopyableId = ({ id, label, className }: CopyableIdProps) => {
  const [copied, setCopied] = useState(false)

  const copy = async (e: React.MouseEvent) => {
    // Copy must not trigger a surrounding row/link handler.
    e.stopPropagation()
    e.preventDefault()
    try {
      await navigator.clipboard?.writeText(id)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch {
      // Clipboard unavailable (insecure context) — degrade silently; the
      // id itself stays visible/selectable next to this control.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${label} ${id}`}
      title={id}
      data-copied={copied ? "true" : "false"}
      className={cn(
        "inline-flex items-center text-text-tertiary/60 transition-colors hover:text-text-secondary",
        className,
      )}
    >
      {copied ? (
        <Check aria-hidden className="size-3.5 text-mint" />
      ) : (
        <Copy aria-hidden className="size-3.5" />
      )}
      <span className="sr-only">{copied ? "copied" : "copy"}</span>
    </button>
  )
}
